/**
 * Projects in Artwork (PROJECTS-MODEL.md W3): what the shared kit (`@flobi/asset-library/projects`)
 * needs from Artwork — the gateway adapter, the Projects app's addresses, each project's file count
 * (`artwork.doc.projects`, which counts only the files the caller can open), and the project names
 * and covers the file cards and the Home's Projects row show.
 *
 * Every call goes through Artwork's one gateway door (`share/gateway.ts` over `apiClient`, which
 * carries the Clerk token and throws the server's own sentence on a refusal). Nothing here sends a
 * workspace or person id: the gateway stamps both.
 */
import { coverOf, createGatewayProjectsAdapter, useProjects, type CoverChoice, type GatewayCall, type ProjectsAdapter } from '@flobi/asset-library/projects';
import type { FolderBrand } from '../ui/ProjectFolderArt';
import { localizeUrl } from '@flobi/asset-library/notifications';
import { gatewayCall } from '../share/gateway';
import { listFiles, listProjectSummaries } from '../home/api';
import type { ProjectInfo, ProjectSummary } from '../home/rows';

const PROJECTS = 'https://projects.flobi.ai';

/** The Projects app's address, rewritten to the local app on a developer's machine. */
function projectsUrl(path: string): string {
  const url = `${PROJECTS}${path}`;
  try {
    return localizeUrl(url) || url;
  } catch {
    return url;
  }
}

/** A project in the Projects app (`/overview/:id`). */
export const projectInProjectsUrl = (id: string) => projectsUrl(`/overview/${encodeURIComponent(id)}`);
/** The Projects app's new-project page, with the name typed so far. */
export const newProjectInProjectsUrl = (name?: string) => projectsUrl(`/new${name ? `?name=${encodeURIComponent(name)}` : ''}`);

const call: GatewayCall = (service, action, data, opts) =>
  gatewayCall(service, action, (data ?? {}) as Record<string, unknown>, opts);

/** The adapter for one person (the workspace is the token's). */
export function artworkProjectsAdapter(me: string | null): ProjectsAdapter {
  return createGatewayProjectsAdapter({ call, me });
}

/**
 * `artwork.doc.projects`, asked once and shared by every card on the page; `forgetSummaries()`
 * drops it (a file made, moved, copied or deleted; the page opened again).
 */
let summaries: Promise<Map<string, ProjectSummary>> | null = null;
function summaryMap(): Promise<Map<string, ProjectSummary>> {
  if (!summaries) {
    summaries = listProjectSummaries()
      .then((list) => new Map(list.map((one) => [one.projectId, one])))
      .catch((err: unknown) => {
        summaries = null;
        throw err;
      });
  }
  return summaries;
}
export const forgetSummaries = () => {
  summaries = null;
};

/** `{ [projectId]: n }` — the files the caller can open in each project. A project with none is 0. */
export async function countFiles(projectIds: string[]): Promise<Record<string, number>> {
  if (!projectIds.length) return {};
  const map = await summaryMap();
  return Object.fromEntries(projectIds.map((id) => [id, map.get(id)?.files ?? 0]));
}

/** How many pages of names one read follows (100 each): a workspace's whole visible list. */
const NAME_PAGES = 10;

/**
 * Every project the caller can see that is shown in Artwork, by id, for the names and covers on
 * file cards and in the editor's My Stuff (`project.list.forApp`, filtered by access and Show in
 * apps — never the whole workspace's list).
 */
export async function listVisibleProjects(): Promise<Map<string, ProjectInfo>> {
  const adapter = artworkProjectsAdapter(null);
  const out = new Map<string, ProjectInfo>();
  let cursor: string | undefined;
  for (let page = 0; page < NAME_PAGES; page++) {
    const res = await adapter.listProjects({ app: 'artwork', tab: 'recent', sort: 'name', limit: 100, ...(cursor ? { cursor } : {}) });
    for (const one of res.items) out.set(one.id, { id: one.id, name: one.name || 'Untitled Project', color: one.color || null, cover: coverOf(one), brandId: one.brands?.[0]?.id ?? null });
    if (!res.nextCursor) break;
    cursor = res.nextCursor;
  }
  return out;
}

/** `{ [projectId]: summary }` — files the caller can open in each project and their newest pictures; none → absent. */
export async function projectSummaries(projectIds: string[]): Promise<Record<string, ProjectSummary | null>> {
  if (!projectIds.length) return {};
  const map = await summaryMap();
  return Object.fromEntries(projectIds.map((id) => [id, map.get(id) ?? null]));
}

/** Project settings › Cover: up to four of the project's Artwork file covers, newest first. */
export async function projectPictures(projectId: string, max = 4): Promise<CoverChoice[]> {
  try {
    const rows = await listFiles({ projectId, limit: 12 });
    const seen = new Set<string>();
    const out: CoverChoice[] = [];
    for (const file of rows) {
      const url = file.thumbnailUrl;
      if (!url || seen.has(url) || url.startsWith('data:')) continue;
      seen.add(url);
      out.push({ url, label: file.name });
      if (out.length >= max) break;
    }
    return out;
  } catch {
    return [];
  }
}

/** The workspace's brand by id (the kit's `brandById`), shaped for the folder's badge. */
export function useBrandOf(): (id: string | null | undefined) => FolderBrand | null {
  const kit = useProjects() as ReturnType<typeof useProjects> & { brandById?: (id: string) => FolderBrand | null };
  return (id) => (id && kit.brandById ? kit.brandById(id) : null);
}
